import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/chat/CallOverlay.jsx
// Experiencia premium de llamadas WebRTC (audio/video/pantalla compartida).
// - Tema oscuro tipo "glass", escenario principal + tira de miniaturas.
// - La pantalla compartida (propia o remota) toma el escenario automáticamente.
// - Indicadores: micrófono, cámara, hablando (detección por AnalyserNode),
//   estado de conexión por participante, banner persistente al compartir.
// - Chat lateral del canal sin salir de la llamada.
// - Sonidos de eventos vía soundManager. Sin servicios externos pagos.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar, Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Fade, IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CallEnd as HangupIcon, Mic as MicIcon, MicOff as MicOffIcon,
  Videocam as CamIcon, VideocamOff as CamOffIcon,
  ScreenShare as ShareIcon, StopScreenShare as StopShareIcon,
  Phone as PhoneIcon, PhoneDisabled as RejectIcon, VolumeOff as SilenceIcon,
  Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon,
  ChatBubbleOutline as ChatIcon, Close as CloseIcon, Send as SendIcon,
  People as PeopleIcon, GridView as GridIcon, SignalCellularAlt as SignalIcon,
  Refresh as RetryIcon, Videocam as VideoIcon,
} from '@mui/icons-material';
import { api } from '../../services/api';
import websocketService from '../../services/websocketService';
import soundManager from '../../services/soundManager';
import { CALL_CONNECT_TIMEOUT_MS, CALL_MAX_PARTICIPANTS, getCallEndReasonLabels } from './chatMediaConstants';

const MAX_PARTICIPANTS = CALL_MAX_PARTICIPANTS;

// ============ Modal de llamada entrante (con tono y silenciar) ============
export const IncomingCallDialog = ({ invite, onAccept, onReject }) => {
  const { t } = useTranslation();
  const [silenced, setSilenced] = useState(false);

  useEffect(() => {
    if (invite?.id) {
      soundManager.startRing(String(invite.id));
      return () => soundManager.stopRing(String(invite.id));
    }
    return undefined;
  }, [invite?.id]);

  const silence = () => { soundManager.stopRing(); setSilenced(true); };
  const isVideo = invite?.call_type === 'video';

  return (
    <Dialog open={!!invite} onClose={() => {}} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: '#16181d', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
          <Avatar sx={{
            width: 84, height: 84, bgcolor: 'primary.main', fontSize: '2rem', fontWeight: 700,
            boxShadow: '0 0 0 0 rgba(211,47,47,0.5)',
            animation: silenced ? 'none' : 'callPulse 1.6s infinite',
            '@keyframes callPulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.45)' },
              '70%': { boxShadow: '0 0 0 22px rgba(211,47,47,0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
            },
          }}>
            {(invite?.by_user_name || '?')[0].toUpperCase()}
          </Avatar>
          <Box sx={{ position: 'absolute', bottom: -2, right: -2, bgcolor: '#16181d', borderRadius: '50%', p: 0.4 }}>
            {isVideo ? <VideoIcon sx={{ color: '#4ade80' }} /> : <PhoneIcon sx={{ color: '#4ade80' }} />}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{invite?.by_user_name || t('audit.table.user')}</Typography>
        <Typography variant="body2" sx={{ color: 'grey.400' }}>
          {isVideo ? t('hardcoded.components_chat_calloverlay.videollamada_entrante') : t('hardcoded.components_chat_calloverlay.llamada_entrante')} · #{invite?.channel_name || t('hardcoded.components_ai_assistantpanel.canal')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
        <Tooltip title={t('hardcoded.components_chat_calloverlay.silenciar_tono')}>
          <IconButton onClick={silence} sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' }}>
            <SilenceIcon />
          </IconButton>
        </Tooltip>
        <Button variant="contained" color="error" startIcon={<RejectIcon />} onClick={onReject}
          sx={{ borderRadius: 6, px: 3 }}>
          {t('hardcoded.components_chat_calloverlay.rechazar')}</Button>
        <Button variant="contained" startIcon={isVideo ? <VideoIcon /> : <PhoneIcon />} onClick={onAccept}
          sx={{ borderRadius: 6, px: 3, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
          {i18n.t('hardcoded.components_chat_calloverlay.contestar')}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Detección de "hablando" por stream ============
const useSpeakingDetector = () => {
  const { t } = useTranslation();
  const ctxRef = useRef(null);
  const analysersRef = useRef({}); // key -> {analyser, src, data}
  const [speaking, setSpeaking] = useState({});

  const attach = useCallback((key, stream) => {
    try {
      if (!stream || !stream.getAudioTracks().length) return;
      if (analysersRef.current[key]) return;
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        ctxRef.current = new AC();
      }
      const src = ctxRef.current.createMediaStreamSource(stream);
      const analyser = ctxRef.current.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analysersRef.current[key] = { analyser, src, data: new Uint8Array(analyser.frequencyBinCount) };
    } catch { /* noop */ }
  }, []);

  const detach = useCallback((key) => {
    const entry = analysersRef.current[key];
    if (entry) { try { entry.src.disconnect(); } catch { /* noop */ } delete analysersRef.current[key]; }
    setSpeaking((p) => { const n = { ...p }; delete n[key]; return n; });
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      const next = {};
      for (const [key, { analyser, data }] of Object.entries(analysersRef.current)) {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        next[key] = (sum / data.length) > 22; // umbral de voz
      }
      setSpeaking((p) => (JSON.stringify(p) === JSON.stringify(next) ? p : next));
    }, 240);
    return () => clearInterval(t);
  }, []);

  useEffect(() => () => { try { ctxRef.current?.close(); } catch { /* noop */ } }, []);

  return { speaking, attach, detach };
};

// ============ Tile de participante ============
const ParticipantTile = ({ stream, name, hasVideo, micOn, isSpeaking, isSelf, isScreen, small, connState }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return (
    <Box sx={{
      position: 'relative', bgcolor: '#101216', borderRadius: 2.5, overflow: 'hidden',
      width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      outline: isSpeaking ? '2.5px solid #4ade80' : '2.5px solid transparent',
      outlineOffset: '-2.5px',
      transition: 'outline-color 0.25s ease',
    }}>
      <video ref={ref} autoPlay playsInline muted={isSelf}
        style={{
          width: '100%', height: '100%',
          objectFit: isScreen ? 'contain' : 'cover',
          display: hasVideo ? 'block' : 'none',
          background: '#000',
        }} />
      {!hasVideo && (
        <Stack alignItems="center" spacing={1}>
          <Avatar sx={{
            width: small ? 42 : 88, height: small ? 42 : 88,
            bgcolor: 'primary.main', fontSize: small ? '1rem' : '2rem', fontWeight: 700,
            boxShadow: isSpeaking ? '0 0 0 5px rgba(74,222,128,0.35)' : 'none',
            transition: 'box-shadow 0.25s ease',
          }}>
            {(name || '?')[0].toUpperCase()}
          </Avatar>
          {!small && <Typography variant="caption" sx={{ color: 'grey.500' }}>{t('hardcoded.components_chat_calloverlay.camara_apagada')}</Typography>}
        </Stack>
      )}
      {/* etiqueta inferior */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{
        position: 'absolute', bottom: 6, left: 8, px: 0.9, py: 0.2,
        bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1.5, backdropFilter: 'blur(4px)',
      }}>
        {micOn === false
          ? <MicOffIcon sx={{ fontSize: small ? 12 : 14, color: '#f87171' }} />
          : <MicIcon sx={{ fontSize: small ? 12 : 14, color: isSpeaking ? '#4ade80' : 'grey.400' }} />}
        <Typography variant="caption" sx={{ color: '#fff', fontSize: small ? '0.62rem' : '0.72rem', fontWeight: 600 }}>
          {isSelf ? t('hardcoded.components_chat_calloverlay.tu') : (name || t('hardcoded.components_chat_calloverlay.participante'))}{isScreen ? t('hardcoded.components_chat_calloverlay.pantalla') : ''}
        </Typography>
        {connState && connState !== 'connected' && !isSelf && (
          <SignalIcon sx={{ fontSize: 12, color: '#fbbf24' }} titleAccess={t("hardcoded.components_chat_calloverlay.reconectando")} />
        )}
      </Stack>
    </Box>
  );
};

// ============ Botón de control redondo ============
const CtrlButton = ({ title, onClick, active, danger, children, badge = 0 }) => (
  <Tooltip title={title}>
    <Badge badgeContent={badge} color="error" overlap="circular">
      <IconButton onClick={onClick} sx={{
        width: 52, height: 52,
        bgcolor: danger ? '#dc2626' : active ? 'primary.main' : 'rgba(255,255,255,0.10)',
        color: '#fff',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.10)',
        transition: 'all 0.18s ease',
        '&:hover': {
          bgcolor: danger ? '#b91c1c' : active ? 'primary.dark' : 'rgba(255,255,255,0.2)',
          transform: 'translateY(-2px)',
        },
      }}>
        {children}
      </IconButton>
    </Badge>
  </Tooltip>
);

// ============ Overlay principal ============
const CallOverlay = ({ call, iceServers, currentUserId, userNamesById = {}, ringTimeout = 45, onClosed }) => {
  const { t } = useTranslation();
  const callEndReasonLabels = useMemo(() => getCallEndReasonLabels(t), [t]);
  const [muted, setMuted] = useState(false);
  const [camOn, setCamOn] = useState(call?.call_type === 'video');
  const [sharing, setSharing] = useState(false);
  const [remoteSharerId, setRemoteSharerId] = useState(null);
  const [peers, setPeers] = useState({});          // userId -> {stream, hasVideo}
  const [peerStates, setPeerStates] = useState({}); // userId -> {mic, cam, sharing}
  const [connStates, setConnStates] = useState({}); // userId -> RTCPeerConnectionState
  const [status, setStatus] = useState('connecting');
  const [permError, setPermError] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [gridMode, setGridMode] = useState(false); // forzar grid aunque haya pantalla
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatBody, setChatBody] = useState('');
  const [chatUnseen, setChatUnseen] = useState(0);

  const rootRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const pcsRef = useRef({});
  const pendingIceRef = useRef({});
  // Reenvío automático del offer cuando la WEB es caller y el peer (p.ej. la APP)
  // se conecta tarde: { uid -> { timer, attempts } }. Sin esto, el offer enviado
  // antes de que la APP esté escuchando se pierde y la llamada queda "Conectando…".
  const offerRetryRef = useRef({});
  const closedRef = useRef(false);
  const aloneTimerRef = useRef(null);
  const connectTimerRef = useRef(null); // timeout de "Conectando…" → failed
  const connectedOnceRef = useRef(false);
  const chatEndRef = useRef(null);

  // ¿Puede este usuario terminar la llamada para TODOS?
  // Permitido a quien la inició o al creador del canal (contrato backend).
  const canEndForAll = useMemo(() => {
    const me = String(currentUserId);
    return me === String(call?.started_by) || me === String(call?.channel_creator_id);
  }, [currentUserId, call?.started_by, call?.channel_creator_id]);
  const isGroup = useMemo(() => (call?.participants || []).length > 2 || call?.is_group, [call]);

  const { speaking, attach: attachSpeak, detach: detachSpeak } = useSpeakingDetector();

  const sendSignal = useCallback((targetUserId, payload) => {
    websocketService.send({ type: 'call_signal', call_id: call.id, target_user_id: targetUserId, payload });
  }, [call.id]);

  // Estado propio (mic/cam/sharing) → avisar a todos los peers
  const broadcastState = useCallback((override = {}) => {
    const state = {
      kind: 'state',
      mic: !muted, cam: camOn, sharing,
      ...override,
    };
    Object.keys(pcsRef.current).forEach((uid) => sendSignal(uid, state));
  }, [muted, camOn, sharing, sendSignal]);

  const closePeer = useCallback((userId) => {
    const pc = pcsRef.current[userId];
    if (pc) { try { pc.close(); } catch { /* noop */ } }
    delete pcsRef.current[userId];
    delete pendingIceRef.current[userId];
    // Detener cualquier reenvío de offer pendiente para este peer.
    if (offerRetryRef.current[userId]) {
      clearTimeout(offerRetryRef.current[userId].timer);
      delete offerRetryRef.current[userId];
    }
    detachSpeak(userId);
    setPeers((p) => { const n = { ...p }; delete n[userId]; return n; });
    setPeerStates((p) => { const n = { ...p }; delete n[userId]; return n; });
    setConnStates((p) => { const n = { ...p }; delete n[userId]; return n; });
    setRemoteSharerId((s) => (s === userId ? null : s));
  }, [detachSpeak]);

  // notifyServer: 'leave' | 'end' | false. 'leave' sale; 'end' termina para todos.
  const teardown = useCallback(async (notifyServer = 'leave', endStatus = null) => {
    if (closedRef.current) return;
    closedRef.current = true;
    clearTimeout(aloneTimerRef.current);
    clearTimeout(connectTimerRef.current);
    // Detener todos los reenvíos de offer pendientes.
    Object.values(offerRetryRef.current).forEach((r) => clearTimeout(r?.timer));
    offerRetryRef.current = {};
    // Detener TODOS los sonidos (ringtone incluido) para no quedar sonando.
    try { soundManager.stopRing(); } catch { /* noop */ }
    soundManager.play('callend');
    // Cerrar todas las conexiones y detener todas las pistas (local + pantalla).
    Object.keys(pcsRef.current).forEach(closePeer);
    [localStreamRef, screenStreamRef].forEach((r) => {
      r.current?.getTracks().forEach((t) => t.stop());
      r.current = null;
    });
    if (notifyServer === 'end') {
      try { await api.chat.endCall(call.id); } catch { try { await api.chat.leaveCall(call.id); } catch { /* noop */ } }
    } else if (notifyServer === 'leave') {
      try { await api.chat.leaveCall(call.id); } catch { /* noop */ }
    }
    onClosed?.(endStatus);
  }, [call.id, closePeer, onClosed]);

  const createPeer = useCallback((userId) => {
    if (pcsRef.current[userId]) return pcsRef.current[userId];
    const pc = new RTCPeerConnection({ iceServers: iceServers || [] });
    pcsRef.current[userId] = pc;

    (localStreamRef.current?.getTracks() || []).forEach((t) => pc.addTrack(t, localStreamRef.current));
    // Si ya estamos compartiendo pantalla, el nuevo peer recibe la pantalla
    if (screenStreamRef.current) {
      const sTrack = screenStreamRef.current.getVideoTracks()[0];
      if (sTrack) {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender) sender.replaceTrack(sTrack);
        else pc.addTrack(sTrack, screenStreamRef.current);
      }
      // Avisar al nuevo peer (incluida la APP móvil) de que ya compartimos.
      setTimeout(() => sendSignal(userId, { kind: 'screen_share_started' }), 500);
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(userId, { kind: 'ice', candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (!stream) return;
      attachSpeak(userId, stream);
      const update = () => setPeers((p) => ({
        ...p,
        [userId]: { stream, hasVideo: stream.getVideoTracks().some((t) => t.readyState === 'live' && !t.muted) },
      }));
      update();
      stream.getVideoTracks().forEach((t) => { t.onmute = update; t.onunmute = update; t.onended = update; });
      // saludo de estado para que el nuevo peer conozca mi mic/cam/pantalla
      setTimeout(() => broadcastState(), 400);
    };
    pc.onconnectionstatechange = () => {
      setConnStates((p) => ({ ...p, [userId]: pc.connectionState }));
      if (pc.connectionState === 'failed') {
        soundManager.play('error');
        closePeer(userId);
      }
    };
    return pc;
  }, [iceServers, sendSignal, closePeer, attachSpeak, broadcastState]);

  // ¿La conexión con este peer ya está establecida (o tiene remote description)?
  // Si es así, no hace falta reenviar el offer.
  const peerSettled = useCallback((pc) => {
    if (!pc) return true;
    if (pc.remoteDescription) return true;
    const cs = pc.connectionState;
    const ics = pc.iceConnectionState;
    return cs === 'connected' || cs === 'completed'
      || ics === 'connected' || ics === 'completed';
  }, []);

  // makeOfferTo es IDEMPOTENTE: si ya existe un peer para ese uid, createPeer lo
  // reutiliza y aquí renegociamos creando un NUEVO offer sobre el MISMO pc.
  // Tras enviar el offer programa un reenvío: si a los ~2.5s seguimos en
  // 'have-local-offer' (la APP aún no respondió porque se conecta tarde), vuelve
  // a ofertar. Hasta 4 reintentos; se detiene al haber remote description /
  // conexión, o al hacer teardown (offerRetryRef se limpia en closePeer/teardown).
  const OFFER_RETRY_MAX = 4;
  const OFFER_RETRY_MS = 2500;

  const makeOfferTo = useCallback(async (userId) => {
    const pc = createPeer(userId);
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendSignal(userId, { kind: 'offer', sdp: pc.localDescription.sdp });
    } catch (e) { console.warn(t('hardcoded.components_chat_calloverlay.offer_error'), e); return; }

    // Programar reenvío automático del offer.
    const entry = offerRetryRef.current[userId] || { timer: null, attempts: 0 };
    clearTimeout(entry.timer);
    const scheduleRetry = () => {
      entry.timer = setTimeout(async () => {
        if (closedRef.current) return;
        const cur = pcsRef.current[userId];
        // Si el peer ya se asentó (remote desc / conectado) dejamos de reintentar.
        if (peerSettled(cur)) { delete offerRetryRef.current[userId]; return; }
        if (entry.attempts >= OFFER_RETRY_MAX) { delete offerRetryRef.current[userId]; return; }
        // Solo reofertar si seguimos esperando respuesta a nuestro offer local.
        if (cur.signalingState === 'have-local-offer' || cur.signalingState === 'stable') {
          entry.attempts += 1;
          try {
            const o = await cur.createOffer();
            await cur.setLocalDescription(o);
            sendSignal(userId, { kind: 'offer', sdp: cur.localDescription.sdp });
          } catch (e) { console.warn(t('hardcoded.components_chat_calloverlay.offer_retry_error'), e); }
        }
        scheduleRetry();
      }, OFFER_RETRY_MS);
    };
    offerRetryRef.current[userId] = entry;
    scheduleRetry();
  }, [createPeer, sendSignal, peerSettled]);

  const flushPendingIce = useCallback(async (userId) => {
    const pc = pcsRef.current[userId];
    const pend = pendingIceRef.current[userId] || [];
    pendingIceRef.current[userId] = [];
    for (const c of pend) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* noop */ }
    }
  }, []);

  // --------- Media local + ofertas iniciales ---------
  const setupMedia = useCallback(async () => {
    setPermError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: call.call_type === 'video',
      });
      localStreamRef.current = stream;
      attachSpeak('self', stream);
      const others = (call.participants || [])
        .map((p) => String(p.user_id))
        .filter((uid) => uid !== String(currentUserId));
      if (others.length === 0) {
        setStatus(t('hardcoded.components_chat_calloverlay.llamando'));
        // Si nadie contesta en ring_timeout → sin respuesta
        aloneTimerRef.current = setTimeout(() => {
          if (Object.keys(pcsRef.current).length === 0 && !closedRef.current) {
            setStatus(t('hardcoded.components_chat_calloverlay.sin_respuesta'));
            setTimeout(() => teardown('leave', 'missed'), 1400);
          }
        }, Math.max(10, ringTimeout) * 1000);
      } else {
        setStatus(t('hardcoded.components_chat_calloverlay.conectando'));
        others.slice(0, MAX_PARTICIPANTS - 1).forEach((uid) => makeOfferTo(uid));
        // Timeout de conexión: si en ~30s no hay ningún peer conectado → fallo,
        // así no se queda "Conectando…" para siempre.
        clearTimeout(connectTimerRef.current);
        connectTimerRef.current = setTimeout(() => {
          if (!connectedOnceRef.current && !closedRef.current) {
            setStatus(callEndReasonLabels.failed);
            soundManager.play('error');
            setTimeout(() => teardown('leave', 'failed'), 1600);
          }
        }, CALL_CONNECT_TIMEOUT_MS);
      }
      setPeers((p) => ({ ...p })); // re-render con video local listo
    } catch (e) {
      setPermError(true);
      setStatus(t('hardcoded.components_chat_calloverlay.microfonocamara_bloqueados'));
      soundManager.play('error');
    }
  }, [call, currentUserId, makeOfferTo, ringTimeout, teardown, attachSpeak]);

  useEffect(() => { setupMedia(); /* eslint-disable-next-line */ }, []);

  // --------- Señalización entrante + eventos de llamada ---------
  useEffect(() => {
    const offSignal = websocketService.on('call_signal', async (data) => {
      if (!data || String(data.call_id) !== String(call.id)) return;
      const from = String(data.from_user_id);
      const payload = data.payload || {};
      if (payload.kind === 'state') {
        setPeerStates((p) => ({ ...p, [from]: { mic: payload.mic, cam: payload.cam, sharing: payload.sharing } }));
        setRemoteSharerId((prev) => {
            if (payload.sharing) { soundManager.play('screenstart'); return from; }
            if (prev === from) { soundManager.play('screenstop'); return null; }
          return prev;
        });
        return;
      }
      // Eventos de pantalla de la APP móvil (mismo contrato dentro de call_signal):
      // 'screen_share_started' / 'screen_share_stopped'. La web web↔web también usa
      // el campo `sharing` del estado, pero la app emite estos eventos dedicados.
      if (payload.kind === 'screen_share_started') {
        setPeerStates((p) => ({ ...p, [from]: { ...(p[from] || {}), sharing: true } }));
        setRemoteSharerId((prev) => { if (prev !== from) soundManager.play('screenstart'); return from; });
        return;
      }
      if (payload.kind === 'screen_share_stopped') {
        setPeerStates((p) => ({ ...p, [from]: { ...(p[from] || {}), sharing: false } }));
        setRemoteSharerId((prev) => { if (prev === from) { soundManager.play('screenstop'); return null; } return prev; });
        return;
      }
      // 'ready': la APP (callee) avisa al unirse que ya está escuchando. La WEB
      // (caller) (re)envía su offer a ese usuario. NO es offer/answer/candidate:
      // nunca debe ir a setRemoteDescription. makeOfferTo es idempotente, así que
      // reutiliza el pc existente y renegocia con un nuevo offer.
      if (payload.kind === 'ready') {
        makeOfferTo(from);
        return;
      }
      const pc = createPeer(from);
      try {
        if (payload.kind === 'offer') {
          await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
          await flushPendingIce(from);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendSignal(from, { kind: 'answer', sdp: pc.localDescription.sdp });
        } else if (payload.kind === 'answer') {
          if (pc.signalingState === 'have-local-offer') {
            await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
            await flushPendingIce(from);
            // El peer respondió: el offer llegó. Cancelar reenvíos pendientes.
            if (offerRetryRef.current[from]) {
              clearTimeout(offerRetryRef.current[from].timer);
              delete offerRetryRef.current[from];
            }
          }
        } else if (payload.kind === 'ice' && payload.candidate) {
          if (pc.remoteDescription) {
            try { await pc.addIceCandidate(new RTCIceCandidate(payload.candidate)); } catch { /* noop */ }
          } else {
            (pendingIceRef.current[from] = pendingIceRef.current[from] || []).push(payload.candidate);
          }
        }
      } catch (e) { console.warn(t('hardcoded.components_chat_calloverlay.signal_error'), e); }
    });

    const offJoined = websocketService.on('chat_call_joined', (data) => {
      if (String(data?.call_id) !== String(call.id)) return;
      if (String(data.user_id) === String(currentUserId)) return;
      clearTimeout(aloneTimerRef.current);
      setStatus(t('hardcoded.components_chat_calloverlay.conectado'));
      soundManager.play('callJoin');
    });
    const offLeft = websocketService.on('chat_call_left', (data) => {
      if (String(data?.call_id) !== String(call.id)) return;
      if (String(data.user_id) === String(currentUserId)) return;
      soundManager.play('callLeave');
      closePeer(String(data.user_id));
      // En 1-1, que el otro salga equivale al fin de la llamada.
      if (!isGroup) {
        setStatus(callEndReasonLabels.hangup);
        setTimeout(() => teardown(false, t('hardcoded.components_chat_calloverlay.ended')), 600);
      }
    });
    const offRejected = websocketService.on('chat_call_rejected', (data) => {
      if (String(data?.call_id) !== String(call.id)) return;
      setStatus(t('hardcoded.components_chat_calloverlay.datauser_nameta_rechazo_la_llamada', { datauser_nameta: data.user_name || t('audit.table.user') }));
      soundManager.play('error');
      // En 1-1, un rechazo termina la llamada saliente.
      if (!isGroup) setTimeout(() => teardown(false, t('hardcoded.components_chat_calloverlay.rejected')), 1200);
    });
    const offEnded = websocketService.on('chat_call_ended', (data) => {
      if (String(data?.call_id) !== String(call.id)) return;
      const reason = data?.reason || t('hardcoded.components_chat_calloverlay.ended');
      // Mostrar el motivo antes de cerrar todo.
      setStatus(callEndReasonLabels[reason] || callEndReasonLabels.ended);
      teardown(false, reason);
    });

    // Chat del canal en vivo durante la llamada
    const offMsg = websocketService.on('chat_message', (data) => {
      if (!data || String(data.channel_id) !== String(call.channel_id)) return;
      setChatMsgs((p) => (p.some((m) => m.id === data.id) ? p : [...p, data]));
      setChatOpen((open) => {
        if (!open && String(data.user_id) !== String(currentUserId)) setChatUnseen((n) => n + 1);
        return open;
      });
    });

    return () => { offSignal?.(); offJoined?.(); offLeft?.(); offRejected?.(); offEnded?.(); offMsg?.(); };
  }, [call.id, call.channel_id, createPeer, makeOfferTo, sendSignal, flushPendingIce, closePeer, teardown, currentUserId, isGroup]);

  // Estado → peers cuando cambian mic/cam/pantalla
  useEffect(() => { broadcastState(); }, [muted, camOn, sharing]); // eslint-disable-line

  // Estado "Conectado" al tener el primer peer
  useEffect(() => {
    if (Object.keys(peers).length > 0) {
      connectedOnceRef.current = true;
      setStatus(t('hardcoded.components_chat_calloverlay.conectado'));
      clearTimeout(aloneTimerRef.current);
      clearTimeout(connectTimerRef.current);
    }
  }, [peers]);

  // Limpieza de timers al desmontar (incluidos los reenvíos de offer)
  useEffect(() => () => {
    clearTimeout(aloneTimerRef.current);
    clearTimeout(connectTimerRef.current);
    Object.values(offerRetryRef.current).forEach((r) => clearTimeout(r?.timer));
    offerRetryRef.current = {};
  }, []);

  // Cargar mensajes del canal al abrir el panel de chat
  useEffect(() => {
    if (!chatOpen) return;
    setChatUnseen(0);
    api.chat.listMessages(call.channel_id, { limit: 30 }).then((r) => setChatMsgs(r.data || [])).catch(() => {});
  }, [chatOpen, call.channel_id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMsgs, chatOpen]);

  // Limpieza al cerrar pestaña
  useEffect(() => {
    const bye = () => { try { api.chat.leaveCall(call.id); } catch { /* noop */ } };
    window.addEventListener('beforeunload', bye);
    return () => window.removeEventListener('beforeunload', bye);
  }, [call.id]);

  // Fullscreen del overlay
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) { await rootRef.current?.requestFullscreen?.(); setFullscreen(true); }
      else { await document.exitFullscreen(); setFullscreen(false); }
    } catch { /* noop */ }
  };
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // --------- Controles ---------
  const toggleMute = () => {
    const next = !muted;
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  const toggleCam = async () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (track) {
      track.enabled = !camOn;
      setCamOn(!camOn);
      return;
    }
    try {
      const cam = await navigator.mediaDevices.getUserMedia({ video: true });
      const vTrack = cam.getVideoTracks()[0];
      stream.addTrack(vTrack);
      await Promise.all(Object.entries(pcsRef.current).map(async ([uid, pc]) => {
        pc.addTrack(vTrack, stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSignal(uid, { kind: 'offer', sdp: pc.localDescription.sdp });
      }));
      setCamOn(true);
    } catch { soundManager.play('error'); }
  };

  const stopShare = useCallback(async () => {
    const wasSharing = !!screenStreamRef.current;
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    const camTrack = localStreamRef.current?.getVideoTracks()[0] || null;
    await Promise.all(Object.entries(pcsRef.current).map(async ([uid, pc]) => {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
      if (sender) {
        // Si la llamada era solo-audio, no había video propio: quitamos el track
        // (replaceTrack(null)) en vez de dejar el de pantalla parado.
        try { await sender.replaceTrack(camTrack); } catch { /* noop */ }
      }
      // Aviso compatible con la APP móvil (mismo evento dentro de call_signal).
      if (wasSharing) sendSignal(uid, { kind: 'screen_share_stopped' });
    }));
    setSharing(false);
    soundManager.play('screenstop');
  }, [sendSignal]);

  const toggleShare = async () => {
    if (sharing) { stopShare(); return; }
    if (remoteSharerId) {
      setStatus(t('hardcoded.components_chat_calloverlay.otro_participante_ya_esta_compartiendo'));
      soundManager.play('error');
      setTimeout(() => setStatus(t('hardcoded.components_chat_calloverlay.conectado')), 2500);
      return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setStatus(t('hardcoded.components_chat_calloverlay.tu_navegador_no_soporta_compartir'));
      soundManager.play('error');
      return;
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const sTrack = display.getVideoTracks()[0];
      screenStreamRef.current = display;
      sTrack.onended = () => stopShare(); // detenido desde el navegador
      await Promise.all(Object.entries(pcsRef.current).map(async ([uid, pc]) => {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender) {
          await sender.replaceTrack(sTrack);
        } else {
          // Llamada solo-audio: no hay m-line de video → añadir y renegociar.
          pc.addTrack(sTrack, display);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendSignal(uid, { kind: 'offer', sdp: pc.localDescription.sdp });
        }
        // Aviso compatible con la APP móvil (mismo evento dentro de call_signal).
        sendSignal(uid, { kind: 'screen_share_started' });
      }));
      setSharing(true);
      soundManager.play('screenstart');
    } catch (e) {
      // NotAllowedError = el usuario canceló el selector → no es un fallo real.
      if (e?.name && e.name !== 'NotAllowedError') {
        setStatus(t('hardcoded.components_chat_calloverlay.no_se_pudo_iniciar_la'));
        soundManager.play('error');
        setTimeout(() => setStatus(t('hardcoded.components_chat_calloverlay.conectado')), 2500);
      }
    }
  };

  const sendChat = async () => {
    const text = chatBody.trim();
    if (!text) return;
    try {
      const r = await api.chat.postMessage(call.channel_id, text, [], []);
      setChatMsgs((p) => (p.some((m) => m.id === r.data.id) ? p : [...p, r.data]));
      setChatBody('');
    } catch { soundManager.play('error'); }
  };

  // --------- Layout: escenario + miniaturas ---------
  const peerEntries = Object.entries(peers);
  const nameOf = (uid) => userNamesById[uid] || t('hardcoded.components_chat_calloverlay.participante');

  // Stream local (cámara o pantalla) para mi miniatura
  const localStream = screenStreamRef.current || localStreamRef.current;
  const localHasVideo = sharing || (camOn && !!localStreamRef.current?.getVideoTracks().length);

  // Escenario principal: pantalla remota > yo compartiendo > hablante activo > primer peer
  const stage = useMemo(() => {
    if (!gridMode) {
      if (remoteSharerId && peers[remoteSharerId]) {
        return { kind: 'remote-screen', uid: remoteSharerId };
      }
      if (sharing) return { kind: 'local-screen' };
    }
    const speakingPeer = peerEntries.find(([uid]) => speaking[uid]);
    if (!gridMode && speakingPeer) return { kind: 'remote', uid: speakingPeer[0] };
    if (!gridMode && peerEntries.length === 1) return { kind: 'remote', uid: peerEntries[0][0] };
    return { kind: 'grid' };
  }, [gridMode, remoteSharerId, peers, sharing, peerEntries, speaking]);

  const filmstrip = useMemo(() => {
    const items = peerEntries
      .filter(([uid]) => !(stage.kind === 'remote' && stage.uid === uid) && !(stage.kind === 'remote-screen' && stage.uid === uid))
      .map(([uid, info]) => ({ key: uid, uid, ...info }));
    return items;
  }, [peerEntries, stage]);

  const gridCols = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(peerEntries.length || 1))));

  const totalInCall = peerEntries.length + 1;

  return (
    <Box ref={rootRef} sx={{
      position: 'fixed', inset: 0, zIndex: 2500,
      background: 'radial-gradient(ellipse at 50% 0%, #1d2027 0%, #0b0c10 70%)',
      display: 'flex', flexDirection: 'column',
      '@media (prefers-reduced-motion: reduce)': { '& *': { animation: 'none !important', transition: 'none !important' } },
    }}>
      {/* ===== Banner persistente: estás compartiendo ===== */}
      {sharing && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
          py: 0.75, bgcolor: 'rgba(34,197,94,0.15)', borderBottom: '1px solid rgba(34,197,94,0.45)',
          backdropFilter: 'blur(8px)',
        }}>
          <Box sx={{
            width: 9, height: 9, borderRadius: '50%', bgcolor: '#22c55e',
            animation: 'sharePulse 1.4s infinite',
            '@keyframes sharePulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
          }} />
          <Typography variant="body2" sx={{ color: '#bbf7d0', fontWeight: 700 }}>
            {t('hardcoded.components_chat_calloverlay.estas_compartiendo_pantalla')}</Typography>
          <Button size="small" variant="outlined" onClick={stopShare}
            sx={{ color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.6)', py: 0, '&:hover': { borderColor: '#22c55e', bgcolor: 'rgba(34,197,94,0.12)' } }}>
            {t('hardcoded.components_chat_calloverlay.dejar_de_compartir')}</Button>
        </Box>
      )}

      {/* ===== Header ===== */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2.5, pt: sharing ? 6 : 2, pb: 1 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800 }}>
              {call.call_type === 'video' ? t('hardcoded.components_chat_calloverlay.videollamada') : t('hardcoded.components_chat_calloverlay.llamada')}
            </Typography>
            {remoteSharerId && (
              <Chip size="small" icon={<ShareIcon sx={{ fontSize: 13 }} />}
                label={t('hardcoded.components_chat_calloverlay.nameofremotesha_esta_compartiendo_pantalla', { nameOfremoteSha: nameOf(remoteSharerId) })}
                sx={{ bgcolor: 'rgba(34,197,94,0.18)', color: '#bbf7d0', border: '1px solid rgba(34,197,94,0.4)', '& .MuiChip-icon': { color: '#4ade80' } }} />
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: 'grey.500' }}>
            {status} · {totalInCall} {i18n.t('hardcoded.components_chat_calloverlay.participante')}{totalInCall !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {(remoteSharerId || sharing) && (
            <Tooltip title={gridMode ? t('hardcoded.components_chat_calloverlay.ver_pantalla_compartida') : t('hardcoded.components_chat_calloverlay.ver_cuadricula_de_participantes')}>
              <IconButton onClick={() => setGridMode((g) => !g)} sx={{ color: 'grey.300' }}>
                <GridIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={fullscreen ? t('hardcoded.components_chat_calloverlay.salir_de_pantalla_completa') : t('hardcoded.components_chat_calloverlay.pantalla_completa')}>
            <IconButton onClick={toggleFullscreen} sx={{ color: 'grey.300' }}>
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* ===== Cuerpo: escenario + chat lateral ===== */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, px: { xs: 1, sm: 2 }, pb: 1, gap: 1.5, position: 'relative' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
          {/* Pantalla de permisos bloqueados */}
          {permError ? (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1 }}>
              <MicOffIcon sx={{ fontSize: 56, color: '#f87171' }} />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                {t('hardcoded.components_chat_calloverlay.microfono_o_camara_bloqueados')}</Typography>
              <Typography variant="body2" sx={{ color: 'grey.400', maxWidth: 420, textAlign: 'center' }}>
                {t('hardcoded.components_chat_calloverlay.permite_el_acceso_al_microfono')}{call.call_type === 'video' ? t('hardcoded.components_chat_calloverlay.y_la_camara') : ''} {t('hardcoded.components_chat_calloverlay.en_el_candado_de_la')}</Typography>
              <Button variant="contained" startIcon={<RetryIcon />} onClick={setupMedia}>{t('profile.tryAgain')}</Button>
            </Stack>
          ) : (
            <>
              {/* Escenario principal */}
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                {stage.kind === 'grid' && (
                  <Box sx={{
                    flex: 1, display: 'grid', gap: 1.25,
                    gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                    alignContent: 'center', maxWidth: 1200, mx: 'auto', width: '100%',
                  }}>
                    {peerEntries.length === 0 && (
                      <Stack alignItems="center" spacing={1.5} sx={{ gridColumn: '1 / -1' }}>
                        <Box sx={{ display: 'flex', gap: 0.8 }}>
                          {[0, 1, 2].map((i) => (
                            <Box key={i} sx={{
                              width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main',
                              animation: 'waitDot 1.2s infinite', animationDelay: `${i * 0.18}s`,
                              '@keyframes waitDot': { '0%,100%': { opacity: 0.25, transform: 'scale(0.8)' }, '50%': { opacity: 1, transform: 'scale(1.1)' } },
                            }} />
                          ))}
                        </Box>
                        <Typography variant="body1" sx={{ color: 'grey.400' }}>{status}</Typography>
                      </Stack>
                    )}
                    {peerEntries.map(([uid, info]) => (
                      <Box key={uid} sx={{ aspectRatio: '4/3', minHeight: 0 }}>
                        <ParticipantTile stream={info.stream} hasVideo={info.hasVideo}
                          name={nameOf(uid)} micOn={peerStates[uid]?.mic !== false}
                          isSpeaking={!!speaking[uid]} connState={connStates[uid]}
                          isScreen={peerStates[uid]?.sharing} />
                      </Box>
                    ))}
                  </Box>
                )}
                {(stage.kind === 'remote' || stage.kind === 'remote-screen') && peers[stage.uid] && (
                  <Box sx={{ flex: 1, maxWidth: 1280, mx: 'auto' }}>
                    <ParticipantTile stream={peers[stage.uid].stream} hasVideo={peers[stage.uid].hasVideo}
                      name={nameOf(stage.uid)} micOn={peerStates[stage.uid]?.mic !== false}
                      isSpeaking={!!speaking[stage.uid]} connState={connStates[stage.uid]}
                      isScreen={stage.kind === 'remote-screen'} />
                  </Box>
                )}
                {stage.kind === 'local-screen' && (
                  <Box sx={{ flex: 1, maxWidth: 1280, mx: 'auto', position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute', inset: 0, borderRadius: 2.5, zIndex: 2, pointerEvents: 'none',
                      boxShadow: 'inset 0 0 0 2.5px rgba(34,197,94,0.7)',
                    }} />
                    <ParticipantTile stream={screenStreamRef.current} hasVideo isSelf isScreen
                      name={t('hardcoded.components_chat_calloverlay.tu_pantalla')} micOn={!muted} isSpeaking={!!speaking.self && !muted} />
                  </Box>
                )}
              </Box>

              {/* Tira de miniaturas (incluye mi propio video) */}
              <Stack direction="row" spacing={1} justifyContent="center"
                sx={{ height: 92, flexShrink: 0, overflowX: 'auto', px: 1, py: 0.25 }}>
                <Box sx={{ width: 124, height: '100%', flexShrink: 0, position: 'relative' }}>
                  <ParticipantTile
                    stream={sharing ? localStreamRef.current : localStream}
                    hasVideo={sharing ? (camOn && !!localStreamRef.current?.getVideoTracks().length) : localHasVideo}
                    name="Tú" isSelf small micOn={!muted}
                    isSpeaking={!!speaking.self && !muted}
                  />
                  {sharing && (
                    <Box sx={{
                      position: 'absolute', top: 4, right: 4, px: 0.6, borderRadius: 1,
                      bgcolor: 'rgba(34,197,94,0.85)', color: '#06280f', fontSize: '0.55rem', fontWeight: 800,
                    }}>
                      {t('hardcoded.components_chat_calloverlay.en_vivo')}</Box>
                  )}
                </Box>
                {filmstrip.map((it) => (
                  <Box key={it.key} sx={{ width: 124, height: '100%', flexShrink: 0 }}>
                    <ParticipantTile stream={it.stream} hasVideo={it.hasVideo} small
                      name={nameOf(it.uid)} micOn={peerStates[it.uid]?.mic !== false}
                      isSpeaking={!!speaking[it.uid]} connState={connStates[it.uid]}
                      isScreen={peerStates[it.uid]?.sharing} />
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>

        {/* ===== Panel lateral de chat ===== */}
        {chatOpen && (
          <Paper sx={{
            // En móvil el chat es un overlay a pantalla completa sobre el escenario;
            // en desktop, un panel lateral fijo de 330px.
            width: { xs: '100%', sm: 330 },
            position: { xs: 'absolute', sm: 'static' }, inset: { xs: 0, sm: 'auto' },
            zIndex: { xs: 20, sm: 'auto' },
            display: 'flex', flexDirection: 'column',
            bgcolor: 'rgba(22,24,29,0.97)', backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: { xs: 0, sm: 2.5 }, overflow: 'hidden',
          }}>
            <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <ChatIcon sx={{ fontSize: 18, color: 'grey.400', mr: 1 }} />
              <Typography variant="subtitle2" sx={{ color: '#fff', flex: 1, fontWeight: 700 }}>{t('hardcoded.components_chat_calloverlay.chat_del_canal')}</Typography>
              <IconButton size="small" onClick={() => setChatOpen(false)} sx={{ color: 'grey.400' }}><CloseIcon fontSize="small" /></IconButton>
            </Stack>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
              {chatMsgs.map((m) => {
                const own = String(m.user_id) === String(currentUserId);
                return (
                  <Box key={m.id} sx={{ mb: 1, textAlign: own ? 'right' : 'left' }}>
                    {!own && <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 700 }}>{m.user_name || m.user_email}</Typography>}
                    <Box sx={{
                      display: 'inline-block', px: 1.2, py: 0.6, borderRadius: 2, maxWidth: '92%',
                      bgcolor: own ? 'rgba(211,47,47,0.25)' : 'rgba(255,255,255,0.08)',
                      border: '1px solid', borderColor: own ? 'rgba(211,47,47,0.4)' : 'rgba(255,255,255,0.08)',
                    }}>
                      <Typography variant="body2" sx={{ color: '#f1f1f1', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {m.body || (m.attachments?.length ? t('hardcoded.components_chat_calloverlay.adjunto') : '')}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              <div ref={chatEndRef} />
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ p: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <TextField
                fullWidth size="small" placeholder={t('hardcoded.components_chat_calloverlay.mensaje')} value={chatBody}
                onChange={(e) => setChatBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff', bgcolor: 'rgba(255,255,255,0.06)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  },
                }}
              />
              <IconButton color="primary" onClick={sendChat} disabled={!chatBody.trim()}><SendIcon /></IconButton>
            </Stack>
          </Paper>
        )}
      </Box>

      {/* ===== Barra de controles ===== */}
      <Fade in>
        <Stack direction="row" spacing={{ xs: 0.75, sm: 1.75 }} justifyContent="center" alignItems="center"
          sx={{
            py: { xs: 1, sm: 1.75 }, mx: 'auto', mb: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 3 },
            borderRadius: 8, flexWrap: 'wrap', maxWidth: '96vw',
            bgcolor: 'rgba(20,22,28,0.72)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          }}>
          <CtrlButton title={muted ? t('hardcoded.components_chat_calloverlay.activar_microfono') : t('hardcoded.components_chat_calloverlay.silenciar_microfono')} onClick={toggleMute} danger={muted}>
            {muted ? <MicOffIcon /> : <MicIcon />}
          </CtrlButton>
          <CtrlButton title={camOn ? t('hardcoded.components_chat_calloverlay.apagar_camara') : t('hardcoded.components_chat_calloverlay.encender_camara')} onClick={toggleCam} active={camOn}>
            {camOn ? <CamIcon /> : <CamOffIcon />}
          </CtrlButton>
          <CtrlButton
            title={sharing ? t('hardcoded.components_chat_calloverlay.dejar_de_compartir_pantalla') : remoteSharerId ? t('hardcoded.components_chat_calloverlay.nameofremotesha_ya_comparte_pantalla', { nameOfremoteSha: nameOf(remoteSharerId) }) : t('hardcoded.components_chat_calloverlay.compartir_pantalla')}
            onClick={toggleShare} active={sharing}>
            {sharing ? <StopShareIcon /> : <ShareIcon />}
          </CtrlButton>
          <CtrlButton title={t('hardcoded.components_chat_calloverlay.chat_del_canal')} onClick={() => setChatOpen((o) => !o)} active={chatOpen} badge={chatUnseen}>
            <ChatIcon />
          </CtrlButton>
          <Tooltip title={t('hardcoded.components_chat_calloverlay.totalincall_en_llamada', { totalInCall: totalInCall })}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'grey.300', px: 0.5 }}>
              <PeopleIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{totalInCall}</Typography>
            </Stack>
          </Tooltip>
          <Box sx={{ width: 10 }} />
          {isGroup && canEndForAll && (
            <Tooltip title={t('hardcoded.components_chat_calloverlay.terminar_la_llamada_para_todos')}>
              <Button onClick={() => teardown('end', 'ended_for_all')} variant="outlined"
                startIcon={<HangupIcon />}
                sx={{
                  height: 52, borderRadius: 7, color: '#fca5a5', borderColor: 'rgba(248,113,113,0.5)',
                  px: 2, fontWeight: 700,
                  '&:hover': { borderColor: '#f87171', bgcolor: 'rgba(248,113,113,0.12)' },
                }}>
                {t('hardcoded.components_chat_calloverlay.terminar_para_todos')}</Button>
            </Tooltip>
          )}
          <Tooltip title={isGroup ? t('hardcoded.components_chat_calloverlay.salir_de_la_llamada') : t('hardcoded.components_chat_calloverlay.colgar')}>
            <IconButton onClick={() => teardown('leave', 'hangup')} sx={{
              width: 60, height: 52, borderRadius: 7, bgcolor: '#dc2626', color: '#fff',
              transition: 'all 0.18s ease',
              '&:hover': { bgcolor: '#b91c1c', transform: 'scale(1.06)' },
            }}>
              <HangupIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Fade>
    </Box>
  );
};

export default CallOverlay;
